import { useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { CompanyMeasurementProvider } from '../../context/CompanyMeasurementProvider'
import { useMeasurements } from '../../context/MeasurementContext'
import { MeasurementSystemSelect } from '../MeasurementSystemSelect'
import type { MeasurementSystem } from '../../lib/measurementSystem'
import type { JobProject } from '../../types/jobProject'
import { jobProjectApi } from '../../services/jobProjectApi'
import { useAuth } from '../../hooks/useAuth'
import Dialog from '../ui/Dialog'

interface Props {
  onClose: () => void
  project?: JobProject
  onSaved?: (project: JobProject) => void
}
function generateReference() {
  const now = new Date()
  const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  return `CHG-${date}-${crypto.randomUUID().slice(0, 6).toUpperCase()}`
}
export default function CreateProjectDialog(props: Props) {
  return <CompanyMeasurementProvider><ProjectForm {...props} /></CompanyMeasurementProvider>
}
function ProjectForm({ onClose, project, onSaved }: Props) {
  const { system } = useMeasurements()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [measurementSystem, setMeasurementSystem] = useState<MeasurementSystem>(project?.measurement_system ?? system)
  const [reference, setReference] = useState(() => project?.reference ?? generateReference())
  const initialReference = useRef(reference)
  const [name, setName] = useState(project?.name ?? '')
  const [description, setDescription] = useState(project?.description ?? '')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submitting = useRef(false)
  const nameInput = useRef<HTMLInputElement>(null)
  const dirty = reference !== initialReference.current || name !== (project?.name ?? '') || description !== (project?.description ?? '') || measurementSystem !== (project?.measurement_system ?? system)
  const requestClose = () => {
    if (!submitting.current && (!dirty || window.confirm('Abandonner les modifications de ce projet ?'))) onClose()
  }
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting.current) return
    if (!reference.trim() || !name.trim()) {
      setError('Renseignez une référence et un nom pour retrouver facilement ce projet.')
      return
    }
    submitting.current = true
    setPending(true)
    setError(null)
    try {
      const values = { reference: reference.trim(), name: name.trim(), description: description.trim() }
      const saved = project
        ? await jobProjectApi.updateProject(project.job_id, values)
        : await jobProjectApi.createProject({ ...values, measurement_system: measurementSystem, tenantId: user?.tenantId || undefined })
      onSaved?.(saved)
      onClose()
      if (!project) navigate('/optimization/new', { state: { jobId: saved.job_id } })
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Enregistrement impossible. Réessayez.')
    } finally {
      submitting.current = false
      setPending(false)
    }
  }
  const fieldClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white'
  return <Dialog isOpen onClose={requestClose} preventClose={pending} initialFocus={nameInput} title={project ? 'Modifier le projet de chargement' : 'Nouveau projet de chargement'}>
    <form onSubmit={handleSubmit} className="space-y-4" aria-busy={pending}>
      <p className="text-sm text-slate-600 dark:text-slate-300">{project ? 'Mettez à jour les informations utilisées pour identifier ce projet.' : 'Nommez votre projet, puis préparez la cargaison, l’équipement et les règles de chargement.'} Les champs marqués * sont obligatoires.</p>
      {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">{error}</p>}
      <fieldset disabled={pending} className="min-w-0 space-y-4">
        <div><label htmlFor="project-name" className="mb-1.5 block text-sm font-semibold">Nom du projet *</label><input id="project-name" ref={nameInput} autoFocus value={name} onChange={event => setName(event.target.value)} placeholder="Ex. Livraison client ABC · Tunis" required maxLength={255} className={fieldClass} /></div>
        <div><label htmlFor="project-reference" className="mb-1.5 block text-sm font-semibold">Référence *</label><div className="flex gap-2"><input id="project-reference" value={reference} onChange={event => setReference(event.target.value)} required maxLength={128} className={`${fieldClass} min-w-0 flex-1`} /><button type="button" onClick={() => setReference(generateReference())} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold hover:bg-orange-50 dark:border-slate-700 dark:hover:bg-slate-800">Générer</button></div><p className="mt-1 text-xs text-slate-500">Référence proposée automatiquement, modifiable selon votre organisation.</p></div>
        <div><label htmlFor="project-description" className="mb-1.5 block text-sm font-semibold">Description <span className="font-normal text-slate-500">(facultatif)</span></label><textarea id="project-description" value={description} onChange={event => setDescription(event.target.value)} maxLength={2000} rows={3} placeholder="Client, tournée ou consignes de préparation…" className={fieldClass} /></div>
        {!project && <MeasurementSystemSelect value={measurementSystem} onChange={setMeasurementSystem} disabled={pending} />}
      </fieldset>
      <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700"><button type="button" disabled={pending} onClick={requestClose} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold dark:border-slate-700">Annuler</button><button type="submit" disabled={pending} className="rounded-xl bg-orange-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-800 disabled:opacity-50">{pending ? 'Enregistrement…' : project ? 'Enregistrer' : 'Créer et préparer la cargaison'}</button></div>
    </form>
  </Dialog>
}
